<template>
  <section class="api-modal-container">
    <Modal
      v-if="selectedSurvey"
      @submit="saveApi"
      @close="closeApiModal"
      :texts="modalTexts"
      width="medium"
    >
      <template v-slot:default>
        <div class="modal-container">
          <h4>{{ $t("please_edit_the_api_information") }}</h4>

          <div class="endpoints-list">
            <div
              class="endpoint-row"
              v-for="(endpoint, idx) in apiSettings.endpoints"
              :key="idx"
            >
              <header class="endpoint-header">
                <h4>{{ $t("endpoint") }} #{{ idx + 1 }}</h4>
                <Button
                  v-if="apiSettings.endpoints.length > 1"
                  skin="accent"
                  @click="removeEndpoint(idx)"
                >
                  {{ $t("remove") }}
                </Button>
              </header>

              <div class="fields-container">
                <template v-if="methods.length > 1">
                  <label>{{ $t("method") }}</label>
                  <Select v-model="endpoint.method" class="filter-select">
                    <Option
                      v-for="method in methods"
                      :key="method"
                      :label="method"
                      :value="method"
                    />
                  </Select>
                </template>

                <label>{{ $t("format") }}</label>
                <Select v-model="endpoint.format" class="filter-select">
                  <Option
                    v-for="f in formats"
                    :key="f"
                    :label="f"
                    :value="f"
                  />
                </Select>

                <label>{{ $t("api_url") }}</label>
                <input v-model.trim="endpoint.url" placeholder="https://" />

                <label>{{ $t("authorization") }}</label>
                <Select
                  v-model="endpoint.auth"
                  class="filter-select"
                  @input="onAuthChange(idx)"
                >
                  <Option
                    v-for="auth in auths"
                    :key="auth"
                    :label="$t(authLabels[auth])"
                    :value="auth"
                  />
                </Select>

                <template v-if="endpoint.auth === 'API KEY'">
                  <label>{{ $t("token") }}</label>
                  <input
                    v-model.trim="endpoint.token"
                    placeholder="Enter Token Here..."
                  />
                </template>

                <template v-if="endpoint.auth === 'DYNAMIC KEY'">
                  <label>{{ $t("dynamic_token_url") }}</label>
                  <input
                    v-model.trim="endpoint.token"
                    placeholder="https://"
                  />
                  <label>{{ $t("token_response_field_name") }}</label>
                  <input
                    v-model.trim="endpoint.tokenDynamicName"
                    placeholder="access_token"
                  />
                </template>

                <template v-if="endpoint.auth === 'LOGIN TOKEN'">
                  <label>{{ $t("token_login_url") }}</label>
                  <input
                    v-model.trim="endpoint.tokenLoginUrl"
                    placeholder="https://"
                  />
                  <label>{{ $t("login_request_method") }}</label>
                  <Select v-model="endpoint.loginMethod" class="filter-select">
                    <Option
                      v-for="lm in loginMethods"
                      :key="lm"
                      :label="lm"
                      :value="lm"
                    />
                  </Select>
                  <template v-if="endpoint.loginMethod === 'POST'">
                    <label>{{ $t("login_request_body") }}</label>
                    <textarea
                      :value="loginBodyText[idx]"
                      class="login-body-json"
                      rows="8"
                      spellcheck="false"
                      :placeholder="loginBodyPlaceholderExample"
                      @input="onLoginBodyInput(idx, $event.target.value)"
                    />
                  </template>
                  <template
                    v-if="
                      endpoint.loginMethod === 'POST' &&
                      loginBodyHasTokenPlaceholder(idx)
                    "
                  >
                    <label>{{ $t("token") }}</label>
                    <input
                      v-model.trim="endpoint.token"
                      :placeholder="$t('login_token_placeholder_hint')"
                    />
                  </template>
                  <label>{{ $t("token_response_key") }}</label>
                  <input
                    v-model.trim="endpoint.tokenResponseKey"
                    placeholder="accessToken"
                  />
                </template>

                <label>{{ $t("survey_api_auth_header_name") }}</label>
                <input
                  v-model="endpoint.headerOptions.key"
                  :placeholder="$t('survey_api_auth_header_name_placeholder')"
                />

                <label>{{ $t("survey_api_auth_header_prefix") }}</label>
                <input
                  v-model="endpoint.headerOptions.value"
                  :placeholder="$t('survey_api_auth_header_prefix_placeholder')"
                />
              </div>
            </div>

            <Button
              skin="primary"
              class="add-endpoint-btn"
              @click="addEndpoint"
            >
              + {{ $t("add_url") }}
            </Button>
          </div>

          <div class="api-info">
            <h4>{{ $t("api_request_info") }}</h4>
            <span v-for="(val, infoName, idx) in apiSettings.info" :key="idx">
              <h4>{{ $t(infoName) }}</h4>
              <CheckBox v-model="apiSettings.info[infoName]" />
            </span>
          </div>
          <div class="api-info">
            <h4>{{ $t("additional_info") }}</h4>
            <span
              v-for="(val, infoName, idx) in apiSettings.additionalInfo"
              :key="idx"
            >
              <h4>{{ $t(infoName) }}</h4>
              <CheckBox v-model="apiSettings.additionalInfo[infoName]" />
            </span>
          </div>
        </div>
      </template>
    </Modal>
  </section>
</template>

<script>
import Modal from "@/modules/common/components/Modal";
import Select from "@/modules/common/components/Select";
import Option from "@/modules/common/components/Option";
import Button from "@/modules/common/components/Button";
import CheckBox from "../components/CheckBox";

function emptyEndpoint() {
  return {
    method: "POST",
    url: "",
    auth: "API KEY",
    token: "",
    tokenDynamicName: "",
    tokenLoginUrl: "",
    loginBody: {},
    loginMethod: "POST",
    tokenResponseKey: "accessToken",
    headerOptions: { key: "", value: "" },
    format: "JSON"
  };
}

function normalizeEndpoint(ep) {
  const source = ep || {};
  const auth = source.auth || "API KEY";

  let loginBody = {};
  if (
    source.loginBody &&
    typeof source.loginBody === "object" &&
    !Array.isArray(source.loginBody) &&
    Object.keys(source.loginBody).length > 0
  ) {
    loginBody = JSON.parse(JSON.stringify(source.loginBody));
  } else if (typeof source.loginBody === "string" && source.loginBody.trim()) {
    try {
      const p = JSON.parse(source.loginBody);
      if (typeof p === "object" && p !== null && !Array.isArray(p)) loginBody = p;
    } catch {
      loginBody = {};
    }
  }

  return {
    method: source.method || "POST",
    url: source.url || "",
    auth,
    token: source.token || "",
    tokenDynamicName: source.tokenDynamicName || "",
    tokenLoginUrl: source.tokenLoginUrl || "",
    loginBody,
    loginMethod: source.loginMethod === "GET" ? "GET" : "POST",
    tokenResponseKey: source.tokenResponseKey || "accessToken",
    headerOptions:
      source.headerOptions && typeof source.headerOptions === "object"
        ? { key: source.headerOptions.key || "", value: source.headerOptions.value || "" }
        : { key: "", value: "" },
    format: (source.format || "JSON").toString().toUpperCase() === "XML" ? "XML" : "JSON"
  };
}

export default {
  props: {
    selectedSurvey: { type: Object, default: null }
  },
  data() {
    return {
      methods: ["POST"],
      loginMethods: ["POST", "GET"],
      auths: ["API KEY", "DYNAMIC KEY", "LOGIN TOKEN"],
      authLabels: {
        "API KEY": "auth_api_key",
        "DYNAMIC KEY": "auth_dynamic_key",
        "LOGIN TOKEN": "auth_login_token"
      },
      formats: ["JSON", "XML"],
      /** Bound from script so Vue does not treat `{{token}}` as template interpolation. */
      loginBodyPlaceholderExample:
        '{"UserName":"...","LongTermToken":"{{token}}"}',
      loginBodyText: ["{}"],
      apiSettings: {
        endpoints: [emptyEndpoint()],
        info: {
          siteName: false,
          roomName: false,
          servikName: false,
          time: false
        },
        additionalInfo: {
          gender: false,
          floor: false,
          zone: false,
          servikCategory: false
        }
      },
      modalTexts: {
        headerPrimary: "api_configuration"
      }
    };
  },
  created() {
    const savedApi = this.selectedSurvey && this.selectedSurvey.api;
    if (!savedApi) return;

    const info = Object.assign({}, this.apiSettings.info, savedApi.info || {});
    const additionalInfo = Object.assign(
      {},
      this.apiSettings.additionalInfo,
      savedApi.additionalInfo || {}
    );

    let endpoints;
    if (Array.isArray(savedApi.endpoints) && savedApi.endpoints.length) {
      endpoints = savedApi.endpoints.map(normalizeEndpoint);
    } else {
      endpoints = [
        normalizeEndpoint({
          method: savedApi.method,
          url: savedApi.url,
          auth: savedApi.auth,
          token: savedApi.token,
          tokenDynamicName: savedApi.tokenDynamicName,
          headerOptions: savedApi.headerOptions,
          format: "JSON"
        })
      ];
    }

    this.apiSettings = { info, additionalInfo, endpoints };
    this.loginBodyText = this.apiSettings.endpoints.map((ep) =>
      JSON.stringify(
        ep.loginBody && typeof ep.loginBody === "object" ? ep.loginBody : {},
        null,
        2
      )
    );
  },
  methods: {
    /** True when login JSON (raw textarea) uses `{{token}}` — backend then requires the secret in `token`. */
    loginBodyHasTokenPlaceholder(idx) {
      const raw = this.loginBodyText[idx];
      return typeof raw === "string" && raw.includes("{{token}}");
    },
    onAuthChange(idx) {
      const ep = this.apiSettings.endpoints[idx];
      if (ep.auth === "LOGIN TOKEN") {
        if (!ep.loginBody || typeof ep.loginBody !== "object") {
          this.$set(ep, "loginBody", {});
        }
        this.$set(
          this.loginBodyText,
          idx,
          JSON.stringify(ep.loginBody, null, 2)
        );
      }
    },
    onLoginBodyInput(idx, raw) {
      this.$set(this.loginBodyText, idx, raw);
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          this.$set(this.apiSettings.endpoints[idx], "loginBody", parsed);
        }
      } catch {
        /* wait until valid JSON */
      }
    },
    addEndpoint() {
      this.apiSettings.endpoints.push(emptyEndpoint());
      this.loginBodyText.push("{}");
    },
    removeEndpoint(idx) {
      if (this.apiSettings.endpoints.length <= 1) return;
      this.apiSettings.endpoints.splice(idx, 1);
      this.loginBodyText.splice(idx, 1);
    },
    saveApi() {
      this.$emit("close", this.apiSettings);
    },
    closeApiModal() {
      this.$emit("close");
    }
  },
  components: {
    Modal,
    Select,
    Option,
    Button,
    CheckBox
  }
};
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.modal-container {
  & > *:not(:last-child) {
    margin-bottom: 20px;
  }

  .endpoints-list {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .endpoint-row {
      @include border($grey-border-color);
      border-radius: 6px;
      padding: 12px 16px;
      background-color: $white;

      .endpoint-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;

        h4 {
          font-weight: 600;
        }
      }

      .fields-container {
        display: grid;
        grid-template-columns: 10vw 1fr;
        column-gap: 12px;
        row-gap: 10px;
        align-items: center;

        label {
          text-align: end;
          font-size: 14px;
        }

        input {
          background-color: $white;
          @include border($grey-border-color);
          border-radius: 4px;
          width: 100%;
          height: 4.2vh;
          padding: 0 14px;
          font-size: 14px;
        }

        textarea.login-body-json {
          background-color: $white;
          @include border($grey-border-color);
          border-radius: 4px;
          width: 100%;
          min-height: 140px;
          padding: 10px 14px;
          font-size: 13px;
          font-family: ui-monospace, Menlo, Monaco, monospace;
          resize: vertical;
        }

        .filter-select {
          background-color: $white;
          @include border($grey-border-color);
          height: 46px;
          border-radius: 4px;
          padding-top: 0;
        }
      }
    }

    .add-endpoint-btn {
      align-self: flex-start;
    }
  }

  .api-info {
    width: 100%;
    @include align-center;
    justify-content: space-between;
    & > *:first-child {
      margin-inline-end: 50px;
    }
    span {
      min-width: 6.1vw;
      @include align-center;
      justify-content: flex-end;
    }
  }
}
</style>
